// JAA-326: Samsung Xclipse GPUs (Exynos Galaxy phones, e.g. S22/S23 FE/S24
// European models) corrupt Mapbox GL symbol rendering in older Google Chrome and
// Chrome Custom Tabs — icons and glyphs draw as skewed streaks. The bug lives
// below the WebGL API (Chrome's ANGLE on top of Samsung's ANGLE-on-Vulkan
// GLES driver), so no app- or library-level change can prevent it. Firefox,
// Edge and Android System WebView render correctly on the same devices.
//
// When this cohort is detected, BuildingMap keeps its symbol layers (so all
// queryRenderedFeatures click/hover logic still works) but hides them and
// mirrors the marker visuals with DOM markers instead — see
// HouseMap/domMarkerFallback.ts.

// JAA-450: verified in released ANGLE source; keep older/unknown builds on
// the fallback. Chrome 152's major-only/reduced UA cannot prove this cutoff.
// https://chromium.googlesource.com/chromium/src/+/refs/tags/152.0.7977.82/DEPS
const FIXED_CHROME_VERSION = [152, 0, 7977, 82];
const VERSION_HINT_TIMEOUT_MS = 500;

interface ChromeBrand {
  brand: string;
  version?: string;
}

interface ChromeUserAgentData {
  brands?: ChromeBrand[];
  getHighEntropyValues?: (hints: string[]) => Promise<{
    fullVersionList?: ChromeBrand[];
  }>;
}

const parseVersion = (version: string | undefined): number[] | null => {
  if (!version || !/^\d+(?:\.\d+){0,3}$/.test(version)) return null;
  const parts = version.split('.').map(Number);
  return parts.every(Number.isSafeInteger) ? parts : null;
};

const hasChromeFix = (version: string | undefined): boolean => {
  const parts = parseVersion(version);
  if (!parts) return false;
  for (let i = 0; i < parts.length; i += 1) {
    if (parts[i] !== FIXED_CHROME_VERSION[i]) {
      return parts[i] > FIXED_CHROME_VERSION[i];
    }
  }
  return parts.length === FIXED_CHROME_VERSION.length;
};

const resolveChromeVersion = async (
  ua: string,
  data: ChromeUserAgentData | undefined,
): Promise<string | undefined> => {
  const uaVersion = /Chrome\/([\d.]+)/.exec(ua)?.[1];
  const version = data?.brands?.find((b) => b.brand === 'Google Chrome')?.version ?? uaVersion;
  const parts = parseVersion(version);
  // Older majors still need protection; newer majors already exceed the
  // cutoff. Only Chrome 152 (or an unknown version) needs a full hint.
  if (parts && parts[0] !== FIXED_CHROME_VERSION[0]) return version;

  const uaParts = parseVersion(uaVersion);
  if (uaParts?.length === 4 && (!parts || uaParts[0] === parts[0]) &&
      (uaParts[2] !== 0 || uaParts[3] !== 0)) {
    return uaVersion;
  }
  if (!data?.getHighEntropyValues) return version;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const values = await Promise.race([
      data.getHighEntropyValues(['fullVersionList']),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), VERSION_HINT_TIMEOUT_MS);
      }),
    ]);
    return values?.fullVersionList?.find((b) => b.brand === 'Google Chrome')?.version ?? version;
  } catch {
    return version;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

const detectXclipseChrome = async (): Promise<boolean> => {
  try {
    const ua = navigator.userAgent;
    // Android System WebView (`wv` token; Messenger/Facebook in-app browsers)
    // renders symbols correctly — exclude it.
    if (/\bwv\b/.test(ua) || /EdgA|Edg\/|OPR|SamsungBrowser|Firefox|DuckDuckGo/i.test(ua)) return false;
    // Chrome + Chrome Custom Tabs only. Edge (EdgA), Samsung Internet, Opera
    // and Firefox were verified unaffected on the same device. Prefer UA
    // Client Hints brands; fall back to UA sniffing where unavailable.
    const data = (
      navigator as Navigator & {
        userAgentData?: ChromeUserAgentData;
      }
    ).userAgentData;
    const brands = data?.brands;
    const isChrome = brands
      ? brands.some((b) => b.brand === 'Google Chrome')
      : /Chrome\//.test(ua);
    if (!isChrome) return false;
    if (hasChromeFix(await resolveChromeVersion(ua, data))) return false;

    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return false;
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = String(
      gl.getParameter(dbg ? dbg.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '',
    );
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return /xclipse/i.test(renderer);
  } catch {
    return false;
  }
};

let cachedDetection: Promise<boolean> | null = null;

// `?domMarkers=1` forces the fallback on anywhere (desktop/QA testing);
// `?domMarkers=0` forces it off on an affected phone (for comparison).
export const domMarkerFallbackEnabled = async (): Promise<boolean> => {
  try {
    const p = new URLSearchParams(window.location.search).get('domMarkers');
    if (p === '1') return true;
    if (p === '0') return false;
  } catch {
    /* non-browser env */
  }
  if (cachedDetection === null) cachedDetection = detectXclipseChrome();
  return cachedDetection;
};
